import React, { useState, useEffect, useCallback } from 'react';
import { apiJson } from '../api';
import { colors, theme } from '../styles';

export default function Analytics({ account }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    if (account) {
      const d = await apiJson(`/accounts/${account.id}/analytics?days=${days}`);
      if (d) setData(d);
    } else {
      const d = await apiJson(`/analytics?days=${days}`);
      if (d) setData(d);
    }
  }, [account, days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div style={{ color: colors.textDim }}>Loading...</div>;

  const summary = account ? (data.summary?.[0] || {}) : null;
  const daily = data.daily || [];
  const stats = data.stats || [];
  const overviewList = !account ? (data.analytics || []) : [];

  // Simple bar chart helper
  const maxVal = (arr, key) => Math.max(1, ...arr.map(d => d[key] || 0));

  const BarChart = ({ data: chartData, keys, height = 160 }) => {
    if (!chartData.length) return <div style={{ color: colors.textDim, fontSize: 12, padding: 16 }}>No data for this period.</div>;
    const barW = Math.max(20, Math.min(50, (600 / chartData.length) - 4));
    const maxH = Math.max(1, ...chartData.map(d => keys.reduce((sum, k) => sum + (d[k.key] || 0), 0)));

    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height, padding: '0 4px', overflow: 'auto' }}>
        {chartData.map((d, i) => {
          let offset = 0;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: barW }}>
              <div style={{ display: 'flex', flexDirection: 'column-reverse', height: height - 24 }}>
                {keys.map(k => {
                  const val = d[k.key] || 0;
                  const h = (val / maxH) * (height - 24);
                  return (
                    <div key={k.key} style={{
                      width: barW - 4, height: h,
                      background: k.color,
                      borderRadius: '2px 2px 0 0',
                    }} title={`${k.label}: ${val}`} />
                  );
                })}
              </div>
              <div style={{ fontSize: 9, color: colors.textDimmer, marginTop: 2, whiteSpace: 'nowrap' }}>
                {d.day?.slice(5) || ''}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>
          Analytics {account ? `: @${account.username}` : '(All Accounts)'}
        </h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              style={{
                ...theme.btnSmall,
                background: days === d ? colors.accent : colors.bgInput,
                color: days === d ? '#fff' : colors.textDim,
                border: `1px solid ${days === d ? colors.accent : colors.borderLight}`,
              }}
              onClick={() => setDays(d)}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* Overview stats for all accounts */}
      {!account && overviewList.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {overviewList.map((a, i) => (
            <div key={i} style={{ ...theme.card, display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <span style={{ color: '#fff', fontWeight: 600 }}>@{a.username}</span>
                <span style={{ ...theme.badge(colors.accent), marginLeft: 8 }}>{a.platform}</span>
              </div>
              <StatBox label="Likes" value={a.total_likes} color={colors.red} />
              <StatBox label="Comments" value={a.total_comments} color={colors.blue} />
              <StatBox label="Follows" value={a.total_follows} color={colors.green} />
              <StatBox label="Unfollows" value={a.total_unfollows} color={colors.orange} />
              <StatBox label="Errors" value={a.total_errors} color={colors.red} />
              <StatBox label="Total" value={a.total_actions} color={colors.accent} />
            </div>
          ))}
        </div>
      )}

      {/* Single account detail */}
      {account && summary && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 16 }}>
            <StatCard label="Likes" value={summary.total_likes} color={colors.red} />
            <StatCard label="Comments" value={summary.total_comments} color={colors.blue} />
            <StatCard label="Follows" value={summary.total_follows} color={colors.green} />
            <StatCard label="Unfollows" value={summary.total_unfollows} color={colors.orange} />
            <StatCard label="Errors" value={summary.total_errors} color={colors.red} />
            <StatCard label="Total" value={summary.total_actions} color={colors.accent} />
          </div>

          {/* Daily chart */}
          <div style={{ ...theme.card, marginBottom: 16 }}>
            <h3 style={theme.sectionTitle}>Daily Activity</h3>
            <BarChart
              data={daily}
              keys={[
                { key: 'likes', color: colors.red, label: 'Likes' },
                { key: 'comments', color: colors.blue, label: 'Comments' },
                { key: 'follows', color: colors.green, label: 'Follows' },
                { key: 'unfollows', color: colors.orange, label: 'Unfollows' },
              ]}
            />
            <div style={{ display: 'flex', gap: 12, marginTop: 8, justifyContent: 'center' }}>
              <Legend color={colors.red} label="Likes" />
              <Legend color={colors.blue} label="Comments" />
              <Legend color={colors.green} label="Follows" />
              <Legend color={colors.orange} label="Unfollows" />
            </div>
          </div>

          {/* Profile stats over time */}
          {stats.length > 0 && (
            <div style={{ ...theme.card }}>
              <h3 style={theme.sectionTitle}>Follower / Following Trend</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                <StatCard
                  label="Current Followers"
                  value={stats[stats.length - 1]?.followers ?? '?'}
                  color={colors.accent}
                />
                <StatCard
                  label="Current Following"
                  value={stats[stats.length - 1]?.following ?? '?'}
                  color={colors.green}
                />
                <StatCard
                  label="Posts"
                  value={stats[stats.length - 1]?.posts ?? '?'}
                  color={colors.blue}
                />
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Followers</th>
                    <th style={thStyle}>Following</th>
                    <th style={thStyle}>Posts</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.slice(-10).reverse().map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.border}11` }}>
                      <td style={tdStyle}>{new Date(s.timestamp).toLocaleDateString()}</td>
                      <td style={tdStyle}>{s.followers ?? '-'}</td>
                      <td style={tdStyle}>{s.following ?? '-'}</td>
                      <td style={tdStyle}>{s.posts ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!account && overviewList.length === 0 && (
        <div style={{ ...theme.card, textAlign: 'center', color: colors.textDim, padding: 32 }}>
          No analytics data yet. Start running automations to collect data.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: colors.bgDark, borderRadius: 6, padding: 12, textAlign: 'center',
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value || 0}</div>
      <div style={{ fontSize: 10, color: colors.textDim, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 50 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value || 0}</div>
      <div style={{ fontSize: 9, color: colors.textDim }}>{label}</div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
      <span style={{ fontSize: 10, color: colors.textDim }}>{label}</span>
    </div>
  );
}

const thStyle = { padding: '6px', textAlign: 'left', fontSize: 10, color: colors.textDim, textTransform: 'uppercase' };
const tdStyle = { padding: '6px', fontSize: 12, color: colors.textMuted };
